import * as XLSX from "xlsx";

const KNOWN_DIRS = new Set(["Inbound", "Outbound", "I/B Proxy", "O/B Proxy"]);

const SHEET_KEYS = {
  "Report @ 2 hrs": "twoHour",
  "Report @ 4 hrs": "fourHour",
};

function cellStr(value) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return "";
  return String(value).trim();
}

/** Excel serial / Date → "26/11/24 4:00 AM" */
function formatIntervalLabel(raw) {
  let dt = null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    dt = raw;
  } else {
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n) || n < 30000) return cellStr(raw);
    // Excel serial days since 1899-12-30
    dt = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
  }
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(dt.getUTCFullYear()).slice(-2);
  let h = dt.getUTCHours();
  const min = String(dt.getUTCMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${dd}/${mm}/${yy} ${h}:${min} ${ampm}`;
}

function isExcelDateSerial(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return true;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 40000;
}

function parseMetricValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : value;
  }
  const s = String(value).trim();
  if (!s) return null;
  const n = Number(s.replace(/,/g, ""));
  if (Number.isFinite(n) && /^-?[\d.,]+$/.test(s)) {
    return Number.isInteger(n) ? n : n;
  }
  return s;
}

function sheetToMatrix(worksheet) {
  return XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: true,
    defval: "",
    blankrows: false,
  });
}

/** Detect VARIANT column + interval start columns from header row 2 (1-based row index 2). */
function detectLayout(matrix) {
  const headerRow = matrix[1] || [];
  let variantCol = 2; // 0-based; default col C
  headerRow.forEach((v, idx) => {
    if (String(v).toUpperCase().includes("VARIANT")) variantCol = idx;
  });

  const intervals = [];
  headerRow.forEach((v, idx) => {
    if (isExcelDateSerial(v)) {
      intervals.push({
        col: idx,
        id: `t${idx}`,
        label: formatIntervalLabel(v),
      });
    }
  });

  return { variantCol, intervals };
}

function classifyRow(ricefId, direction) {
  const a = ricefId || "";
  const b = direction || "";
  if (a === "Benchmarking from prior years" || a.toLowerCase().startsWith("benchmark")) {
    return "section";
  }
  if (a.startsWith("TOTAL ")) return "total";
  if (
    a.startsWith("No of Sales") ||
    a.startsWith("Number of Sales") ||
    a.startsWith("ZORDERS")
  ) {
    return "summary";
  }
  if (b.includes("Proxy") || b === "I/B Proxy" || b === "O/B Proxy") return "proxy";
  return "idoc";
}

function buildReportFromMatrix(matrix, reportKey) {
  const row2 = matrix[1] || [];
  const row3 = matrix[2] || [];
  const subtitle =
    cellStr(row2[0]) ||
    (reportKey === "twoHour"
      ? "2 Hourly Monitoring (12X/day)"
      : "4 Hourly Monitoring (6X/day)");
  const caption = cellStr(row3[0]) || "No of Idocs - cumulative by day";
  const groupHeader =
    cellStr((matrix[0] || []).find((v, i) => i > 0 && cellStr(v))) ||
    "No of Idocs - cumulative by day (starting 00:00)";

  const { variantCol, intervals: detected } = detectLayout(matrix);
  const intervals = detected;

  let metricHeaders = ["Success", "Error", "Total", "Pending"];
  if (intervals.length) {
    const base = intervals[0].col;
    const mh = [0, 1, 2, 3].map((off) => cellStr(row3[base + off]));
    if (mh[0].toLowerCase().startsWith("success")) {
      metricHeaders = mh.map((m, i) => m || metricHeaders[i]);
    }
  }

  const rows = [];
  for (let r = 3; r < matrix.length; r += 1) {
    const row = matrix[r] || [];
    const ricefId = cellStr(row[0]);
    const direction = cellStr(row[1]);
    const variant = cellStr(row[variantCol]);
    if (!ricefId && !direction && !variant) continue;

    const type = classifyRow(ricefId, direction);
    if (type === "section") {
      rows.push({
        type: "section",
        ricefId: ricefId || "Benchmarking from prior years",
        direction: "",
        directionKey: "",
        variant: "",
        metrics: {},
      });
      continue;
    }

    const firstLine = direction.split("\n")[0].trim();
    const directionKey = KNOWN_DIRS.has(firstLine) ? firstLine : "";

    const metrics = {};
    intervals.forEach((iv) => {
      const vals = [0, 1, 2, 3].map((off) => parseMetricValue(row[iv.col + off]));
      metrics[iv.id] = {
        success: vals[0],
        error: vals[1],
        total: vals[2],
        pending: vals[3],
      };
    });

    rows.push({
      type,
      ricefId,
      direction,
      directionKey,
      variant,
      metrics,
    });
  }

  return {
    reportTitle: "Thanks Giving Monitoring",
    reportKey,
    subtitle,
    caption,
    groupHeader,
    metricHeaders,
    asOf: "",
    intervals: intervals.map(({ id, label }) => ({ id, label })),
    rows,
  };
}

/**
 * Parse Thanksgiving monitoring workbook (ArrayBuffer / Uint8Array).
 * Returns { twoHour, fourHour } report objects (missing sheets omitted).
 */
export function parseThanksgivingWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false });
  const reports = {};

  workbook.SheetNames.forEach((name) => {
    const key = SHEET_KEYS[name];
    if (!key) return;
    const worksheet = workbook.Sheets[name];
    if (!worksheet) return;
    const matrix = sheetToMatrix(worksheet);
    if (!matrix.length) return;
    reports[key] = buildReportFromMatrix(matrix, key);
  });

  return reports;
}

/** Parse a single-interval workbook returned by fetch_thanksgiving_monitoring_report. */
export function parseThanksgivingWorkbookForInterval(arrayBuffer, interval) {
  const reportKey = String(interval).trim() === "4" ? "fourHour" : "twoHour";
  const fromCombined = parseThanksgivingWorkbook(arrayBuffer);
  if (fromCombined[reportKey]) {
    return { [reportKey]: fromCombined[reportKey] };
  }

  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return {};

  const worksheet = workbook.Sheets[sheetName];
  const matrix = sheetToMatrix(worksheet);
  if (!matrix.length) return {};

  return { [reportKey]: buildReportFromMatrix(matrix, reportKey) };
}

/** Derive default From/To ISO dates from interval labels across reports. */
export function defaultDateRangeFromReports(reports) {
  const dates = [];
  Object.values(reports || {}).forEach((report) => {
    (report.intervals || []).forEach((iv) => {
      const m = String(iv.label || "")
        .trim()
        .match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/);
      if (!m) return;
      const [, dd, mm, yy] = m;
      const year = yy.length === 2 ? `20${yy}` : yy;
      dates.push(`${year}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`);
    });
  });
  dates.sort();
  if (!dates.length) {
    const today = new Date();
    const iso = today.toISOString().slice(0, 10);
    return { start: iso, end: iso };
  }
  return { start: dates[0], end: dates[dates.length - 1] };
}
